import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { db } from '../lib/cloudsql';

interface Template {
  id: string;
  name: string;
  storage_path: string;
  variables: string[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

const QuoteTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<Template | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await db.query('quote_templates', {
        orderBy: [{ field: 'created_at', direction: 'desc' }]
      });
      setTemplates((data || []) as Template[]);
    } catch {
      // Error fetching templates
    } finally {
      setLoading(false);
    }
  };

  const createNewTemplate = () => {
    const newTemplate: Omit<Template, 'id' | 'created_at' | 'updated_at'> = {
      name: '',
      storage_path: '',
      variables: [],
      is_default: false
    };
    setCurrentTemplate(newTemplate as Template);
    setIsEditing(false);
    setShowCreateModal(true);
  };

  const editTemplate = (template: Template) => {
    setCurrentTemplate(template);
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const saveTemplate = async () => {
    if (!currentTemplate) return;

    // 驗證必填欄位
    if (!currentTemplate.name.trim()) {
      alert('請輸入模板名稱');
      return;
    }

    try {
      if (isEditing) {
        await db.update('quote_templates', currentTemplate.id!, currentTemplate as Template);
      } else {
        await db.create('quote_templates', currentTemplate as Template);
      }

      setShowCreateModal(false);
      setCurrentTemplate(null);
      fetchTemplates();
      alert('模板儲存成功！');
    } catch {
      // Error saving template
      alert('儲存失敗，請檢查資料是否正確');
    }
  };

  const deleteTemplate = async (templateId: string) => {
    if (!confirm('確定要刪除此模板嗎？')) return;
    
    try {
      await db.delete('quote_templates', templateId);
      fetchTemplates();
    } catch {
      // Error deleting template
    }
  };

  const setDefaultTemplate = async (templateId: string) => {
    try {
      // 先將所有模板設為非預設
      const allTemplates = await db.query<{ id: string }>('quote_templates', {});
      for (const template of allTemplates) {
        if (template.id !== templateId) {
          await db.update('quote_templates', template.id, { is_default: false });
        }
      }

      // 設定選中的模板為預設
      await db.update('quote_templates', templateId, { is_default: true });
      fetchTemplates();
    } catch {
      // Error setting default template
    }
  };

  const getStatusColor = (isDefault: boolean) => {
    return isDefault ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (isDefault: boolean) => {
    return isDefault ? '預設' : '一般';
  };

  const getStatusIcon = (isDefault: boolean) => {
    return isDefault ? <CheckCircleIcon className="w-4 h-4" /> : <DocumentTextIcon className="w-4 h-4" />;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">報價單模板管理</h1>
        <button
          onClick={createNewTemplate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
        >
          <PlusIcon className="w-5 h-5" />
          新增模板
        </button>
      </div>

      {/* 模板列表 */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    模板資訊
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    狀態
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    變數
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    建立日期
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {template.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {template.storage_path}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.is_default)}`}>
                        {getStatusIcon(template.is_default)}
                        <span className="ml-1">{getStatusText(template.is_default)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {template.variables.map((variable, index) => (
                          <span
                            key={index}
                            className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                          >
                            {variable}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(template.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => editTemplate(template)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {!template.is_default && (
                          <button
                            onClick={() => setDefaultTemplate(template.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 新增/編輯模板模態框 */}
      {showCreateModal && currentTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">
                {isEditing ? '編輯模板' : '新增模板'}
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* 基本資訊 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  模板名稱 *
                </label>
                <input
                  type="text"
                  required
                  value={currentTemplate.name}
                  onChange={(e) => setCurrentTemplate({...currentTemplate, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  儲存路徑 *
                </label>
                <input
                  type="text"
                  required
                  value={currentTemplate.storage_path}
                  onChange={(e) => setCurrentTemplate({...currentTemplate, storage_path: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="templates/quote_template.docx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  變數列表
                </label>
                <textarea
                  value={currentTemplate.variables.join(', ')}
                  onChange={(e) => {
                    const variables = e.target.value.split(',').map(v => v.trim()).filter(v => v);
                    setCurrentTemplate({...currentTemplate, variables});
                  }}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="QUOTE_NUMBER, PROJECT_NAME, CUSTOMER_NAME, ..."
                />
                <p className="mt-1 text-sm text-gray-500">
                  用逗號分隔變數名稱
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={currentTemplate.is_default}
                    onChange={(e) => setCurrentTemplate({...currentTemplate, is_default: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">設為預設模板</span>
                </label>
              </div>

              {/* 按鈕 */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={saveTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  儲存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteTemplates; 